import api from 'api';
import { get } from 'utils/fetch';
import { useState, useEffect, useContext } from 'react';
import { message, Spin, Input, Button } from 'antd';
import { PropertyInsertion } from 'pages/RecordInsertion/PropertyInsertion';
import { isPhoneSize } from 'utils/util';
import { GlobalContext } from 'GlobalContext';
import { TransactionDetailed } from './TransactionDetailed';
import { isAdmin, isSale } from 'utils/util';
import { USDollar } from 'utils/format';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';

function TableHeader() {
    return (
        <div
            style={{
                display: 'flex',
                borderBottom: '1px solid black',
                width: 'fit-content',
                alignItems: 'stretch',
            }}
        >
            <HeaderCell width={60}></HeaderCell>
            <HeaderCell width={120}>Purchase Price</HeaderCell>
            <HeaderCell width={160}>Under Contract Date</HeaderCell>
            <HeaderCell width={180}>Transaction Status</HeaderCell>
            <HeaderCell width={160}>Purchase Close Date</HeaderCell>
            <HeaderCell width={200}>Title Binder Info</HeaderCell>
            <HeaderCell width={200}>Title History</HeaderCell>
            <HeaderCell width={160}>1031 Company Info</HeaderCell>
            <HeaderCell width={160}>Target Exit Date</HeaderCell>
            <HeaderCell width={200}>Relisting Agent</HeaderCell>
            <HeaderCell width={120}>Sold Price</HeaderCell>
            <HeaderCell width={140}>Sold Date</HeaderCell>
        </div>
    );
}

function HeaderCell({ width, children }) {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                fontWeight: 'bold',
                whiteSpace: 'pre-wrap',
                borderRight: '1px solid #ddd',
                boxSizing: 'border-box',
                width: width ? `${width}px` : 'auto',
                minWidth: width ? `${width}px` : 'auto',
                // maxWidth removed so it can grow if needed
                flex: `0 0 ${width}px`, // keeps column aligned
                wordBreak: 'break-word', // for very long words
                textAlign: 'center',
                height: 'auto',
                alignSelf: 'stretch',
            }}
        >
            {children}
        </div>
    );
}

function Th({ width, children }) {
    return (
        <th
            style={{
                width,
                minWidth: width,
                padding: '8px',
                borderRight: '1px solid #ddd',
                textAlign: 'center',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                background: '#f9f9f9',
            }}
        >
            {children}
        </th>
    );
}

function Td({ width, children }) {
    return (
        <td
            style={{
                width,
                minWidth: width,
                padding: '8px',
                borderRight: '1px solid #eee',
                textAlign: 'center',
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </td>
    );
}

function TransactionTable({ transactions }) {
    const { assetManager } = useContext(GlobalContext);
    const { setTransactionId } = assetManager;

    return (
        <div
            style={{
                overflow: 'auto',
                maxHeight: 'calc(100vh - 80px)', // vertical scroll
                border: '1px solid #ddd',
                width: '100%',
            }}
        >
            <table
                style={{
                    borderCollapse: 'collapse',
                    width: 'max-content',
                    minWidth: '100%',
                }}
            >
                {/* Sticky header */}
                <thead
                    style={{
                        position: 'sticky',
                        top: 0,
                        background: 'white',
                        zIndex: 2,
                        borderBottom: '2px solid black',
                    }}
                >
                    <tr>
                        <Th width={60}>Edit</Th>
                        <Th width={120}>Purchase Price</Th>
                        <Th width={160}>Under Contract Date</Th>
                        <Th width={180}>Transaction Status</Th>
                        <Th width={160}>Purchase Close Date</Th>
                        <Th width={200}>Title Binder Info</Th>
                        <Th width={200}>Title History</Th>
                        <Th width={160}>1031 Company Info</Th>
                        <Th width={160}>Target Exit Date</Th>
                        <Th width={200}>Relisting Agent</Th>
                        <Th width={120}>Sold Price</Th>
                        <Th width={140}>Sold Date</Th>
                    </tr>
                </thead>

                <tbody>
                    {transactions
                        .sort((a, b) => {
                            if (
                                a.under_contract_date !== b.under_contract_date
                            ) {
                                return a.under_contract_date <
                                    b.under_contract_date
                                    ? -1
                                    : 1;
                            }
                            return a.id - b.id;
                        })
                        .map((transaction) => (
                            <tr key={transaction.id}>
                                <Td width={60}>
                                    <Button
                                        type="link"
                                        icon={<EditOutlined />}
                                        onClick={() =>
                                            setTransactionId(transaction.id)
                                        }
                                    />
                                </Td>
                                <Td width={120}>
                                    {USDollar.format(
                                        transaction.purchase_price
                                    )}
                                </Td>
                                <Td width={160}>
                                    {transaction.under_contract_date}
                                </Td>
                                <Td width={180}>
                                    {transaction.transaction_status}
                                </Td>
                                <Td width={160}>
                                    {transaction.purchase_close_date}
                                </Td>
                                <Td width={200}>
                                    {transaction.title_binder_info}
                                </Td>
                                <Td width={200}>{transaction.title_history}</Td>
                                <Td width={160}>
                                    {transaction['1031_company_info']}
                                </Td>
                                <Td width={160}>
                                    {transaction.target_exit_date}
                                </Td>
                                <Td width={200}>
                                    {transaction.relisting_agent}
                                </Td>
                                <Td width={120}>
                                    {USDollar.format(transaction.sold_price)}
                                </Td>
                                <Td width={140}>{transaction.sold_date}</Td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
}

export function TransactionInfo() {
    const {
        manageAssetId,
        setManageAssetId,
        accessToken,
        assetManager,
        refreshSession,
    } = useContext(GlobalContext);
    const { transactionId, setTransactionId } = assetManager;
    const [transactions, setTransactions] = useState([]);
    const [messageApi, contextHolder] = message.useMessage();
    const [loading, setLoading] = useState(
        manageAssetId < 0 || transactionId !== 0 ? false : true
    );
    const [propertyTransactionInfo, setPropertyTransactionInfo] =
        useState(null);

    // 0: overview, -1: add transaction, > 0: check existing transaction

    const entryStyle = {
        margin: '20px',
        fontSize: '20px',
    };

    const inputStyle = {
        border: '0',
        outline: 'none',
        borderBottom: '1px solid black',
        fontSize: '20px',
        background: 'transparent',
    };

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    async function fetchTransactions(assetId) {
        await get(
            api.asset.getTransactions(assetId),
            setTransactions,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        refreshSession();
        setLoading(false);
    }

    useEffect(() => {
        if (transactionId === 0 && manageAssetId > 0) {
            setLoading(true);
            fetchTransactions(manageAssetId);
        }
    }, [manageAssetId, transactionId]);

    return (
        <>
            {loading && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <Spin />
                </div>
            )}
            {!loading && (
                <>
                    {transactionId !== 0 && (
                        <div
                            style={
                                isPhoneSize()
                                    ? {
                                          height: 'calc(100vh - 310px)',
                                          overflowY: 'scroll',
                                          scrollbarWidth: 'none',
                                          overflowX: 'hidden',
                                      }
                                    : {
                                          height: 'calc(100vh - 30px)',
                                          overflowY: 'scroll',
                                          scrollbarWidth: 'none',
                                          overflowX: 'hidden',
                                      }
                            }
                        >
                            <TransactionDetailed />
                        </div>
                    )}
                    {transactionId === 0 && (
                        <>
                            {isAdmin() && (
                                <div style={{ marginTop: '10px' }}>
                                    <Button
                                        type="default"
                                        icon={<PlusOutlined />}
                                        onClick={() => setTransactionId(-1)}
                                    >
                                        Add
                                    </Button>
                                </div>
                            )}
                            <TransactionTable transactions={transactions} />
                        </>
                    )}
                </>
            )}
        </>
    );
}
